import {
  Client,
  ClientOptions,
  GraphError,
  GraphRequest,
  PageIterator,
} from '@microsoft/microsoft-graph-client';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { ItemIsNotSavedException } from 'common/exceptions/application/ItemIsNotSavedException';
import { OutlookApiException } from 'common/exceptions/outlook/OutlookApiException';
import { OutlookItemNotSupported } from 'common/exceptions/outlook/OutlookItemNotSupported';
import { AppointmentUids } from 'common/models/AppointmentUids';
import { GraphCollection } from 'common/models/graph/GraphCollection';
import CONFIG from 'config';
import { StatusCodes } from 'http-status-codes';
import { GraphAuthenticationProvider } from './graphAuthUtils';
import { getItemRestId } from './outlookItemUtils';

const TAG = 'GRA1: ';

const extendedPropertiesPropertyId = `String {00020329-0000-0000-C000-000000000046} Name cecp-${CONFIG.appId}`;

const expandExtendedProperties = `singleValueExtendedProperties($filter=id eq '${extendedPropertiesPropertyId}')`;

const contactFoldersCache = new Map<string, MicrosoftGraph.ContactFolder>();

function filterExtendedProperties(propertyValue?: string): string {
  return (
    `singleValueExtendedProperties/Any(` +
    `ep: ep/id eq '${extendedPropertiesPropertyId}'` +
    (propertyValue !== undefined
      ? ` and contains(ep/value, '${propertyValue}')`
      : '') +
    `)`
  );
}

const client = Client.initWithMiddleware({
  authProvider: new GraphAuthenticationProvider(),
});

export async function getOrCreateContactGroup(name: string) {
  let group = await getContactGroup(name);
  if (!group) {
    if (contactFoldersCache.has(name)) {
      group = contactFoldersCache.get(name);
    } else {
      group = await createContactGroup(name);
      if (group) {
        contactFoldersCache.set(name, group);
      }
    }
  }
  return group;
}

async function createContactGroup(name: string) {
  console.log(TAG, 'Creating contact group ', name);

  try {
    const response: MicrosoftGraph.ContactFolder = await client
      .api('/me/contactFolders')
      .post(<MicrosoftGraph.ContactFolder>{ displayName: name });

    console.log(TAG, 'Created contact group ', response);

    return response;
  } catch (error) {
    throw new OutlookApiException(
      'Failed to create contact group with Graph API ',
      error
    );
  }
}

async function getContactGroup(name: string) {
  console.log(TAG, 'Getting contact group ', name);
  try {
    const response: GraphCollection<MicrosoftGraph.ContactFolder> = await client
      .api('/me/contactFolders')
      .get();

    const groups: MicrosoftGraph.ContactFolder[] = [];

    const iterator = new PageIterator(
      client,
      response,
      (group: MicrosoftGraph.ContactFolder) => {
        groups.push(group);
        return true;
      }
    );

    await iterator.iterate();

    console.log(TAG, 'Contact groups ', groups);

    const group = groups.find((g) => g.displayName === name);
    console.log(TAG, 'Contact group is ', group);
    return group;
  } catch (error) {
    throw new OutlookApiException(
      'Failed to get contact groups from Graph API ',
      error
    );
  }
}

export async function getContacts(groupId: string) {
  try {
    const response: GraphCollection<MicrosoftGraph.Contact> = await client
      .api(`/me/contactFolders/${groupId}/contacts`)
      .get();

    const contacts = response.value;
    return contacts;
  } catch (error) {
    throw new OutlookApiException(
      'Failed to get contacts from Graph API ',
      error
    );
  }
}

export async function getContact(email: string, contactGroupName: string) {
  const group = await getOrCreateContactGroup(contactGroupName);
  const contacts = await getContacts(group.id);

  const existingContact = contacts.find(
    (c) => c.emailAddresses[0] && c.emailAddresses[0].address === email
  );

  return existingContact;
}

export async function createOrUpdateContact(
  contact: MicrosoftGraph.Contact,
  contactGroupName: string
) {
  const group = await getOrCreateContactGroup(contactGroupName);
  const existingContact = await getContact(
    contact.emailAddresses[0].address,
    contactGroupName
  );

  if (!existingContact) {
    await createContact(group.id, contact);
  } else {
    contact.id = existingContact.id;
    await updateContact(contact);
  }
}

export async function updateContact(contact: MicrosoftGraph.Contact) {
  console.log(TAG, 'Updating contact ', contact.emailAddresses[0].address);

  try {
    await client.api(`/me/contacts/${contact.id}`).patch(contact);
  } catch (error) {
    throw new OutlookApiException(
      'Failed to update contact with Graph API ',
      error
    );
  }
}

async function createContact(
  contactGroupId: string,
  contact: MicrosoftGraph.Contact
) {
  console.log(TAG, 'Creating contact ', contact.emailAddresses[0].address);

  try {
    await client
      .api(`/me/contactFolders/${contactGroupId}/contacts`)
      .post(contact);
  } catch (error) {
    throw new OutlookApiException(
      'Failed to create contact with Graph API ',
      error
    );
  }
}

export async function deleteContactGroup(contactGroupName: string) {
  console.log(TAG, 'Deleting contacts from ', contactGroupName);
  const group = await getContactGroup(contactGroupName);
  if (!group) return;

  const contacts = await getContacts(group.id);
  for (const c of contacts) {
    await deleteContact(c.id);
  }
}

export async function deleteContact(contactId: string) {
  console.log(TAG, 'Deleting contact ', contactId);

  try {
    await client.api(`/me/contacts/${contactId}`).delete();
  } catch (error) {
    throw new OutlookApiException(
      'Failed to delete contact with Graph API ',
      error
    );
  }
}

export async function getMessages(addressesToDates: Map<string, Date>) {
  if (!addressesToDates.size) return [];
  const inbox = await getMessagesFromFolder('inbox', addressesToDates);
  const sent = await getMessagesFromFolder('sentItems', addressesToDates);

  let allMessages = [...inbox, ...sent];

  allMessages = allMessages.filter((m, i) => {
    return allMessages.findIndex((m2) => m2.id === m.id) === i;
  });

  return allMessages;
}

async function getMessagesFromFolder(
  folder: 'inbox' | 'sentItems',
  addressesToDates: Map<string, Date>
) {
  const requests: GraphRequest[] = [];

  const path = `/me/mailFolders/${folder}/messages`;

  const extrasLength =
    (client['config'] as ClientOptions).baseUrl.length +
    path.length +
    '?search=""'.length +
    '&$top=200'.length +
    134;

  let fullSearchExpression = '';
  addressesToDates.forEach((lastSyncDate: Date, address: string) => {
    const currentExpression =
      `((from:${address} OR to:${address}) AND ` +
      `(received>=${lastSyncDate.toISOString()} OR sent>=${lastSyncDate.toISOString()}))`;

    const combinedExpression = encodeURIComponent(
      `"${fullSearchExpression}${currentExpression}"`
    );

    // if full length of URL exceeds OData request length the limit
    // (with subtracted length for $skipToken for next page requests),
    // then add the URL without that expression to the request queue
    // and reset the current expression
    if (combinedExpression.length >= 2047 - extrasLength) {
      const request = client
        .api(path)
        .search(`"${fullSearchExpression}"`)
        .top(200);

      requests.push(request);
      fullSearchExpression = currentExpression;
    } else {
      // if there is already something in the search expression, add 'OR'
      if (fullSearchExpression) {
        fullSearchExpression += ' OR ' + currentExpression;
      } else {
        fullSearchExpression = currentExpression;
      }
    }
  });

  const request = client.api(path).search(`"${fullSearchExpression}"`).top(200);

  requests.push(request);

  const allMessages: MicrosoftGraph.Message[] = [];
  for (const request of requests) {
    const messages = await getAllPagedMessages(request);
    allMessages.push(...messages);
  }

  return allMessages;
}

/**
 * Makes requests for all pages and combines them into one array
 */
async function getAllPagedMessages(
  request: GraphRequest
): Promise<MicrosoftGraph.Message[]> {
  const messages: MicrosoftGraph.Message[] = [];
  console.log(TAG, 'Making request for messages: ', request);

  const response: GraphCollection<MicrosoftGraph.Message> = await request.get();

  const iterator = new PageIterator(
    client,
    response,
    (message: MicrosoftGraph.Message) => {
      messages.push(message);
      return true;
    }
  );

  try {
    await iterator.iterate();
  } catch (error: unknown) {
    throw new OutlookApiException(
      'Failed to get messages from Graph API',
      error
    );
  }

  return messages;
}

export async function getEvent(
  itemId: string,
  sharedProperties: Office.SharedProperties | undefined
): Promise<MicrosoftGraph.Event> {
  try {
    const response: MicrosoftGraph.Event = await client
      .api(
        `/${getUserGraphUrlSegment(sharedProperties)}/events/${getItemRestId(
          itemId
        )}`
      )
      .get();

    return response;
  } catch (error) {
    throw new OutlookApiException('Failed to get event from Graph API', error);
  }
}

/**
 * Note: The returned {@link AppointmentUids.recurrenceId} property will be different between this method and any legacy EWS ID
 * for the same event.
 */
export async function getAppointmentUids(
  itemId: string,
  sharedProperties: Office.SharedProperties | undefined
): Promise<AppointmentUids> {
  try {
    const event = await getEvent(itemId, sharedProperties);

    if (event.type === 'occurrence') {
      const seriesMaster = await getEvent(
        event.seriesMasterId,
        sharedProperties
      );

      return {
        uid: seriesMaster.iCalUId,
        recurrenceId: event.iCalUId,
        itemType: event.type,
      };
    }

    return {
      uid: event.iCalUId,
      recurrenceId: null,
      itemType: event.type,
    };
  } catch (error: unknown) {
    if (
      error instanceof OutlookApiException &&
      (error.apiResponse as GraphError).statusCode === StatusCodes.NOT_FOUND
    ) {
      console.log(TAG, 'Appointment not found: not saved?');
      // this occurs when we were able get item id from the Exchange Server,
      // but the since user hasn't yet saved the draft item,
      // Exchange Server is still refusing to acknowledge existence of the item with this id
      throw new ItemIsNotSavedException();
    }

    throw new OutlookApiException(
      'Failed to get appointment Uid from Graph API',
      error
    );
  }
}

/** Searches all Appointments by custom property value. Returns ID of first Appointment with this property */
export async function getFirstEventIdByCustomProperty(
  propertyValue: string,
  sharedProperties: Office.SharedProperties | undefined
) {
  try {
    const response: GraphCollection<MicrosoftGraph.Event> = await client
      .api(`${getUserGraphUrlSegment(sharedProperties)}/events`)
      .filter(filterExtendedProperties(propertyValue))
      .expand(expandExtendedProperties)
      .get();

    const events = response.value;

    if (!events || events.length === 0) {
      return null;
    }

    return events[0].id;
  } catch (error) {
    throw new OutlookApiException(
      'Failed to get events by custom property from Graph API',
      error
    );
  }
}

export async function getAttachments(
  itemId: string,
  itemType: string,
  sharedProperties: Office.SharedProperties | undefined
) {
  const restItemId = getItemRestId(itemId);

  const userSegment = getUserGraphUrlSegment(sharedProperties);

  let request: GraphRequest;
  if (itemType === 'message') {
    request = client.api(`/${userSegment}/messages/${restItemId}/attachments`);
  } else if (itemType === 'appointment') {
    request = client.api(`/${userSegment}/events/${restItemId}/attachments`);
  } else {
    throw new OutlookItemNotSupported();
  }

  console.log(TAG, 'Getting attachments from', request);

  const response: GraphCollection<MicrosoftGraph.Attachment> =
    await request.get();

  return response.value;
}

export async function getExtendedProperty(
  message: MicrosoftGraph.Message,
  propertyName: string
) {
  const appExtendedProperties = await getAppExtendedProperties(message);
  if (!appExtendedProperties) return undefined;
  const propertyValue = appExtendedProperties[propertyName];
  return propertyValue;
}

async function getAppExtendedProperties(message: MicrosoftGraph.Message) {
  try {
    const response: MicrosoftGraph.Message = await client
      .api(`/me/messages/${message.id}`)
      .expand(expandExtendedProperties)
      .get();

    const appExtendedProperties = response.singleValueExtendedProperties?.find(
      (prop) => prop.id === extendedPropertiesPropertyId
    );

    if (appExtendedProperties) {
      return <Record<string, unknown>>JSON.parse(appExtendedProperties.value);
    }

    return undefined;
  } catch (error) {
    throw new OutlookApiException(
      'Failed to get extended properties from Graph API: ' + error.message,
      error
    );
  }
}

export async function setExtendedProperty(
  message: MicrosoftGraph.Message,
  propertyName: string,
  propertyValue: string
) {
  try {
    let currentProperties = await getAppExtendedProperties(message);

    if (!currentProperties) currentProperties = {};
    currentProperties[propertyName] = propertyValue;

    const json = JSON.stringify(currentProperties);

    const newMessage: MicrosoftGraph.Message = {
      singleValueExtendedProperties: [
        {
          id: extendedPropertiesPropertyId,
          value: json,
        },
      ],
    };

    await client.api(`/me/messages/${message.id}`).patch(newMessage);
  } catch (error) {
    throw new OutlookApiException(
      'Failed to set extended property with Graph API: ' + error.message,
      error
    );
  }
}

export function getUserGraphUrlSegment(
  sharedProperties: Office.SharedProperties | undefined
) {
  return sharedProperties ? `/users/${sharedProperties.targetMailbox}` : '/me';
}
